import BaseDOM, {
	BASE_ELEMENT_IDS as BASE_ELEMENT_IDS,
	createButton,
	getTranslationName,
} from "../BaseDOM";
import { inject, injectable } from "inversify";
import SERVICE_IDENTIFIER from "../../constants/identifiers";
import SystemConfig, { CONFIG_PARAMS, MODES } from "../../system/SystemConfig";
import IUIMessages from "../messages/IUIMessages";

import { noUiSlider, create as createNoUiSlider } from "nouislider";
import { getNoUISliderRange as getRange } from "../../helpers";
import IMadmaxMessage from "../messages/IMadmaxMessage";
import {
	getAndroidVersion,
	getDeviceStr,
	isAndroid,
	isFirefox,
	isIpad,
	isIphoneChrome,
	isIphoneFirefox,
	isIphoneSafari,
} from "../../device/device";
import { ACTION_EVENTS } from "../UserAction";
import { createInfopages, createPreinfoClose } from "./components/infopages";
import SoundOnOff from "./components/SoundOnOff";
import createInfoBoxes from "./components/infoboxes";
import BgLayer from "../components/BgLayer";
import { translate } from "../../translations/translations";
import Wallet from "../../wallet/Wallet";
import IGame from "../../game/IGame";
import AbstractAudio from "../../engine/AbstractAudio";
import FullscreenSwiper from "./components/FullscreenSwiper";
import { MESSAGE_EVENTS } from "../../error/errorMessages";
import events from "../../events/EventsObservable";
import SettingsMenu from "./components/SettingsMenu";
import { EVENTS } from "../../events/baseEvents";

export const ELEMENT_IDS = {
	BUTTON_SPIN_WRAP: "mxButtonSpinWrap",

	BAR: "mxBar",
	BAR_BLACK: "mxBarBlack",
	BAR_GREY: "mxBarGrey",
	BAR_FREESPINS: "mxBarFreespins",

	BALANCE: "mxBalanceReal",
	CREDIT: "mxBalanceCredit",
	BET_REAL: "mxBetReal",
	BET_CREDIT: "mxBetCredit",
	WIN_REAL: "mxWinReal",
	BET_LINE: "mxBetLine",
	FREE_SPINS: "mxFreeSpins",
	LINES: "mxLines",
	BAR_LOGO: "mxBarLogo",

	BUTTON_BET_OPEN: "mxButtonBetOpen",
	BUTTON_BET_CLOSE: "mxButtonBetClose",
	BUTTON_AUTOSTART_OPEN: "mxButtonAutostartOpen",
	BUTTON_AUTOSTART_CLOSE: "mxButtonAutostartClose",
	BUTTON_AUTOSTART_CONTINUE: "mxButtonAutostartContinue",
	BUTTON_SETTINGS_CLOSE: "mxButtonSettingsClose",
	BUTTON_BURGER: "mxButtonBurger",
	BUTTON_BURGER_INFO: "mxButtonBurgerInfo",
	BUTTON_BURGER_SOUND: "mxBurgerButtonSound",
	BUTTON_BURGER_HANDEDNESS: "mxButtonHandedness",
	BUTTON_BURGER_SETTINGS: "mxButtonSettings",
	BUTTON_FREESPINS: "mxButtonFreespins",

	MENU_BET: "mxBetMenu",
	MENU_AUTOSTART: "mxAutostartMenu",
	MENU_BURGER: "mxBurgerMenu",
	MENU_BURGER_UL: "mxBurgerMenuUL",
	MENU_SETTINGS: "mxSettingsMenu",

	MENU_SETTINGS_BUTTON_CONTAINER: "mxButtonSettingsBtnContainer",
	BUTTON_SETTINGS_SPIN: "mxButtonSettingsSpin",
	BUTTON_SETTINGS_RESET: "mxButtonSettingsReset",
	BUTTON_SETTINGS_SAVE: "mxButtonsSettingsSave",

	SWIPER_OVERLAY_IOS: "mxIOSSwiperOverlay",
	SWIPER_WRAPPER: "mxSwiperWrapper",
	SWIPER_HAND: "mxSwiperHand",

	SLIDER_BET: "mxSliderBet",
	SLIDER_LINES: "mxSliderLines",
	SLIDER_COIN: "mxSliderCoin",
	SLIDER_AUTOSTART: "mxSliderAutostart",

	BET_IN_CASH: "mxBetInCash",
	BET_IN_COINS: "mxBetInCoins",

	SOUNDONOFF: "mxSoundOnOff",

	INFO_BUTTON_LEFT: "mxInfoButtonLeft",
	INFO_BUTTON_RIGHT: "mxInfoButtonRight",
	INFO_BUTTON_CLOSE: "mxInfoButtonClose",
};

@injectable()
export default class MobileDOM extends BaseDOM {
	public betSlider: noUiSlider.API;
	public autostartSlider: noUiSlider.API;
	public coinSlider: noUiSlider.API;
	public linesSlider: noUiSlider.API;
	public soundChooseMessage: IMadmaxMessage;
	public bg: BgLayer;

	private audio: AbstractAudio;
	public spinBtnHoldTimeout: any;

	public leftHanded: boolean; // is UI left handed
	public freeSpinsShown: boolean; // true - freespins are visible, false - freespins are disabled
	private fsButton: HTMLElement;
	private fsMode: "real" | "freespins" = "real";
	private betBarElement: HTMLElement;

	fullscreenSwiper: FullscreenSwiper;
	settingsMenu: SettingsMenu = undefined;

	protected winToBalanceRunning: boolean = false;
	protected winToBalancePromises: Array<Promise<any>> = [];

	respins: HTMLElement|undefined = undefined

	// path of numbers during winToBalance animation
	portraitPath: Array<Array<number>> = [
		[0, 0, 5, 3, 10, 4],
		[10, 4, 15, 3, 20, 0],
	]; // in vh
	landscapePath: Array<Array<number>> = [
		[0, 0, 6.25, 3, 12.5, 4],
		[12.5, 4, 18.75, 3, 25, 0],
	]; // in vw

	constructor(
		@inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
		@inject(SERVICE_IDENTIFIER.UIMessages) uiMessages: IUIMessages,
		@inject(SERVICE_IDENTIFIER.Wallet) wallet: Wallet,
		@inject(SERVICE_IDENTIFIER.Game) game: IGame,
		@inject(SERVICE_IDENTIFIER.EngineAudio) audio: AbstractAudio
	) {
		super(config, uiMessages, wallet, game);
		this.getCssStyles();

		this.audio = audio;

		this.leftHanded = false;

		events.subscribe(EVENTS.GAME_LOAD_SUCCESS, () => {
			this.autostartSlider?.on("slide", this.sliderUpdate.bind(this));
			this.betSlider?.on("slide", this.sliderUpdate.bind(this));
			this.linesSlider?.on("slide", this.sliderUpdate.bind(this));
			this.coinSlider?.on("slide", this.sliderUpdate.bind(this));
		});
	}

	getCssStyles() {
		$("head").append(
			'<link rel="stylesheet" type="text/css" href="mobile.css">'
		);
	}

	createDOM() {
		super.createDOM();

		// DISABLE contect menu on click+hold
		document.addEventListener(
			"contextmenu",
			function (e) {
				e.preventDefault();
			},
			false
		);

		// add X-handed class
		this.wrapper.classList.add(
			this.leftHanded ? "left-handed" : "right-handed"
		);

		// DISABLE ZOOM
		var meta = document.createElement("meta");
		meta.name = "viewport";
		meta.content =
			"width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0";
		document.getElementsByTagName("head")[0].appendChild(meta);

		let bar = this.createBar(this.gameset);
		this.gameControlsWrapper.appendChild(bar);

		this.bg = new BgLayer(this.gameControlsWrapper);
		// this.gameControlsWrapper.appendChild(this.bg.element);

		this.createButtons(this.gameControlsWrapper, this.spinBtns);

		this._createAutostartMenu(this.gameControlsWrapper);
		this._createBetMenu(this.gameControlsWrapper);

		createInfopages(this.wintableButtonsContainer);

		createPreinfoClose(
			document.getElementById(BASE_ELEMENT_IDS.GAME_BUTTONS_CONTAINER)
		);

		this.soundChooseMessage = new SoundOnOff();
		document
			.getElementById(BASE_ELEMENT_IDS.GAME_BUTTONS_CONTAINER)
			.appendChild(this.soundChooseMessage.getElement());
		this.soundChooseMessage.closeMessage();

		//this.uiMessages.addMessage(this.soundChooseMessage);

		this.createInfoboxes();

		if (this.isSwiperEnabled()) {
			this.fullscreenSwiper = new FullscreenSwiper(
				this.config.get(CONFIG_PARAMS.SWIPER)
			);
			this.fullscreenSwiper.enable(false);
		}
	}

	protected createInfoboxes() {
		createInfoBoxes(
			this.gameControlsWrapper,
			this.gameset.gameName,
			this.config
		);
	}

	protected _createBurgerMenu(parent) {
		let container = document.createElement("div");
		container.classList.add("burger-menu-container");
		container.id = ELEMENT_IDS.MENU_BURGER;
		parent.appendChild(container);

		container.addEventListener("click", (event) => {
			event.stopPropagation();
			events.dispatch(ACTION_EVENTS.ACTION_BURGER_CLOSE);
			// document.getElementById(ELEMENT_IDS.BUTTON_BURGER).click()
		});

		let ul = document.createElement("ul");
		ul.classList.add("burger-menu");
		ul.id = ELEMENT_IDS.MENU_BURGER_UL;
		container.appendChild(ul);

		let info = document.createElement("li");
		info.id = ELEMENT_IDS.BUTTON_BURGER_INFO;
		info.classList.add(
			"mx-button",
			"mx-input",
			"burger-menu-info",
			"burger-button"
		);
		$(info).data("action", ACTION_EVENTS.ACTION_INFO_TOGGLE);
		ul.appendChild(info);

		let handedness = document.createElement("li");
		handedness.id = ELEMENT_IDS.BUTTON_BURGER_HANDEDNESS;
		handedness.classList.add(
			"mx-button",
			"mx-input",
			"burger-menu-handedness",
			"burger-button"
		);
		$(handedness).data("action", ACTION_EVENTS.ACTION_HANDEDNESS_TOGGLE);
		$(handedness).data("data", "left");
		ul.appendChild(handedness);

		let sound = document.createElement("li");
		sound.id = ELEMENT_IDS.BUTTON_BURGER_SOUND;
		sound.classList.add(
			"mx-button",
			"mx-input",
			"burger-button",
			this.audio.isMuted() ? "mx-ico-sound-off" : "mx-ico-sound-on"
		);
		$(sound).data("action", ACTION_EVENTS.ACTION_SOUND_TOGGLE);
		ul.appendChild(sound);

		if (this.config.get(CONFIG_PARAMS.HOME_SETTINGS)?.mobile === true) {
			let home = document.createElement("li");
			home.classList.add(
				"mx-button",
				"mx-input",
				"burger-menu-home",
				"burger-button"
			);
			$(home).data("action", ACTION_EVENTS.ACTION_HOME_BUTTON);
			ul.appendChild(home);
		}

		if (this.config.get(CONFIG_PARAMS.MODE) === MODES.REAL) {
			if (
				this.config.get(CONFIG_PARAMS.URL_HISTORY_PARAM) ||
				this.config.get(CONFIG_PARAMS.IS_HISTORY)
			) {
				let history = document.createElement("li");
				history.classList.add(
					"mx-button",
					"mx-input",
					"burger-menu-history",
					"burger-button"
				);
				$(history).data("action", ACTION_EVENTS.ACTION_HISTORY_OPEN);
				ul.appendChild(history);
			}
		}

		if (this.config.get(CONFIG_PARAMS.IS_PARAGRAPH)) {
			let paragraph = document.createElement("li");
			paragraph.classList.add(
				"mx-button",
				"mx-input",
				"burger-menu-paragraph",
				"burger-button"
			);
			$(paragraph).data("action", ACTION_EVENTS.ACTION_PARAGRAPH_OPEN);
			ul.appendChild(paragraph);
		}

		$(container).hide();
	}

	init() {
		super.init();

		this._createBurgerMenu(this.gameControlsWrapper);
	}

	sliderUpdate() {
		events.dispatch(ACTION_EVENTS.ACTION_SLIDER_SLIDE);
	}

	private _createBetMenu(parent: HTMLElement) {
		let wrapper = document.createElement("div");
		wrapper.id = ELEMENT_IDS.MENU_BET;
		wrapper.classList.add("bet-menu");
		parent.appendChild(wrapper);

		let close = document.createElement("button");
		close.id = ELEMENT_IDS.BUTTON_BET_CLOSE;
		close.classList.add(
			"mx-input",
			"mx-button",
			"mx-button-close-menu",
			"mx-icon-check"
		);
		wrapper.appendChild(close);
		$(close).data("action", ACTION_EVENTS.ACTION_BETMENU_CLOSE);

		let slidersContainer = document.createElement("div");
		slidersContainer.classList.add("bet-menu-sliders-container");
		wrapper.appendChild(slidersContainer);

		this._initBetSlider(
			this._createSlider(slidersContainer, "bet", ELEMENT_IDS.SLIDER_BET)
		);
		this._initCoinSlider(
			this._createSlider(
				slidersContainer,
				"coin_value",
				ELEMENT_IDS.SLIDER_COIN
			)
		);
		this._initLinesSlider(
			this._createSlider(
				slidersContainer,
				getTranslationName("line", this.gameset),
				ELEMENT_IDS.SLIDER_LINES
			)
		);

		let infoContiner = document.createElement("div");
		infoContiner.classList.add("mx-betmenu-info");
		wrapper.appendChild(infoContiner);

		let betInCash = document.createElement("div");
		betInCash.classList.add("mx-bet-infobox");
		let betInCashTitle = document.createElement("span");
		betInCashTitle.classList.add("mx-translate");
		// betInCashTitle.innerHTML = "Bet in cash:";
		betInCashTitle.setAttribute("data-lang", "bet_in_cash");
		betInCash.appendChild(betInCashTitle);
		let betInCashValue = document.createElement("span");
		betInCashValue.classList.add("mx-bet-infobox-value");
		betInCashValue.id = ELEMENT_IDS.BET_IN_CASH;
		betInCash.appendChild(betInCashValue);
		infoContiner.appendChild(betInCash);

		let betInCoins = document.createElement("div");
		betInCoins.classList.add("mx-bet-infobox");
		let betInCoinsTitle = document.createElement("span");
		betInCoinsTitle.classList.add("mx-translate");
		// betInCoinsTitle.innerHTML = "Bet in coins:";
		betInCoinsTitle.setAttribute("data-lang", "bet_in_coins");
		betInCoins.appendChild(betInCoinsTitle);
		let betInCoinsValue = document.createElement("span");
		betInCoinsValue.classList.add("mx-bet-infobox-value");
		betInCoinsValue.id = ELEMENT_IDS.BET_IN_COINS;
		betInCoins.appendChild(betInCoinsValue);
		infoContiner.appendChild(betInCoins);

		$(wrapper).hide();
	}

	private _createAutostartMenu(parent: HTMLElement) {
		if (!this.config.get(CONFIG_PARAMS.DISABLE_AUTOSTART)) {
			let wrapper = document.createElement("div");
			wrapper.id = ELEMENT_IDS.MENU_AUTOSTART;
			wrapper.classList.add("autostart-menu");
			parent.appendChild(wrapper);

			let close = document.createElement("button");
			close.id = ELEMENT_IDS.BUTTON_AUTOSTART_CLOSE;
			close.classList.add(
				"mx-input",
				"mx-button",
				"mx-button-close-menu",
				"mx-icon-check"
			);
			wrapper.appendChild(close);

			$(close).data("action", ACTION_EVENTS.ACTION_AUTOSTART_CLOSE);

			$(wrapper).hide();

			this._initAutostartSlider(
				this._createSlider(wrapper, "autostart", ELEMENT_IDS.SLIDER_AUTOSTART)
			);
		}
	}

	getDefaultRange() {
		let range = this.game.autostart?.getValues();

		// if range contains Infinity, replace it for 1000
		range.indexOf(Infinity) !== -1 &&
			range.splice(range.indexOf(Infinity), 1, 1000);
		return range;
	}

	private _initAutostartSlider(element) {
		let range = this.getDefaultRange();
		// add 0 as first element
		range.unshift(0);

		let text = translate("off", this.config.get(CONFIG_PARAMS.LANGUAGE));
		this.autostartSlider = createNoUiSlider(element, {
			// tooltips: true,
			start: 0,
			connect: "lower",
			snap: true,
			range: getRange(range),
			pips: {
				mode: "steps",
				format: {
					to: (v) => (v == 0 ? text : v == 1000 ? "" : v),
					from: (v) => parseInt(v),
				},
			},
		});

		element.querySelectorAll(".noUi-value").forEach((el) => {
			el.addEventListener("click", (e) => {
				element.noUiSlider.set(
					parseInt(e.target.getAttribute("data-value"), 10)
				);
				this.sliderUpdate();
			});
		});
	}

	private _createSlider(parent, transl, id) {
		let container = document.createElement("div");
		container.id = id + "Container";
		container.classList.add("mx-slider-container");
		parent.appendChild(container);

		let wrap = document.createElement("div");
		wrap.classList.add("mx-slider");
		container.appendChild(wrap);

		let title = document.createElement("div");
		title.classList.add("mx-slider-title", "mx-translate");
		title.setAttribute("data-lang", transl);
		// title.innerText = name;
		wrap.appendChild(title);

		let slider = document.createElement("div");
		slider.id = id;
		wrap.appendChild(slider);

		return slider;
	}

	private _initBetSlider(element) {
		this.betSlider = createNoUiSlider(element, {
			start: 1,
			connect: [true, false],
			snap: true,
			range: getRange([1, 2, 3, 4, 5]),
			pips: {
				mode: "steps",
				format: { to: (v) => this.getSpan(v), from: (v) => parseInt(v) },
			},
			format: {
				to: (v) => this.sliderVal(v, 0),
				from: (v) => this.sliderVal(v, 0),
			},
		});
	}

	private _initCoinSlider(element) {
		this.coinSlider = createNoUiSlider(element, {
			start: 1,
			connect: [true, false],
			snap: true,
			range: { min: 0, max: 1 }, //getValues([0.1, 0.2, 0.5, 1, 2, 5, 10]),
			pips: {
				mode: "steps",
				// values: [0.1, 0.2, 0.5, 1, 2, 5, 10],
				format: { to: (v) => this.getSpan(v), from: (v) => parseFloat(v) },
			},
			format: {
				to: (v) => this.sliderVal(v, this.wallet.getDecimalPlaces()),
				from: (v) => this.sliderVal(v, this.wallet.getDecimalPlaces()),
			},
		});
	}

	private _initLinesSlider(element) {
		this.linesSlider = createNoUiSlider(element, {
			start: 1,
			connect: [true, false],
			snap: true,
			range: getRange([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
			pips: {
				mode: "steps",
				format: { to: (v) => this.getSpan(v), from: (v) => parseFloat(v) },
			},
			format: {
				to: (v) => this.sliderVal(v, 0),
				from: (v) => this.sliderVal(v, 0),
			},
		});
	}

	sliderVal(val: any, digits: number): number {
		return Number((val * 1).toFixed(digits));
	}

	getSpan(val) {
		return `<span>${val}</span>`;
	}

	createBar(gameset): HTMLElement {
		let bar: HTMLElement = document.createElement("div");
		bar.classList.add("mx-bar");
		bar.id = ELEMENT_IDS.BAR;

		// $(bar).insertBefore(controls);

		let barFs = document.createElement("div");
		barFs.id = ELEMENT_IDS.BAR_FREESPINS;
		barFs.classList.add("mx-bar-freespins");
		$(barFs).hide();
		bar.appendChild(barFs);

		let fs = document.createElement("div");
		fs.classList.add("mx-bar-freespins-text");
		let fsTitle = document.createElement("span");
		fsTitle.classList.add("mx-translate", "mx-bar-title");
		fsTitle.setAttribute("data-lang", "freespins");
		fs.appendChild(fsTitle);
		let fsValue = document.createElement("span");
		fsValue.id = ELEMENT_IDS.FREE_SPINS;
		fs.appendChild(fsValue);
		barFs.appendChild(fs);

		this.hideFreeSpins();

		let barGrey = document.createElement("div");
		barGrey.id = ELEMENT_IDS.BAR_GREY;
		barGrey.classList.add("mx-bar-grey");
		bar.appendChild(barGrey);

		let credit = document.createElement("div");
		credit.classList.add("mx-bar-creditbalance");
		let creditTitle = document.createElement("span");
		creditTitle.classList.add("mx-translate", "mx-bar-title");
		creditTitle.setAttribute("data-lang", "credit");
		credit.appendChild(creditTitle);
		let creditValue = document.createElement("span");
		creditValue.id = ELEMENT_IDS.CREDIT;
		credit.appendChild(creditValue);
		barGrey.appendChild(credit);

		let betLine = document.createElement("div");
		betLine.id = ELEMENT_IDS.BET_LINE + "Container";
		betLine.classList.add("mx-bar-betline");
		let betLineTitle = document.createElement("span");
		betLineTitle.classList.add("mx-translate", "mx-bar-title");
		betLineTitle.setAttribute(
			"data-lang",
			getTranslationName("bet_line", gameset)
		);
		betLine.appendChild(betLineTitle);
		let betLineValue = document.createElement("span");
		betLineValue.id = ELEMENT_IDS.BET_LINE;
		betLine.appendChild(betLineValue);
		barGrey.appendChild(betLine);

		let creditBet = document.createElement("div");
		creditBet.classList.add("mx-bar-creditbet");
		let creditBetTitle = document.createElement("span");
		creditBetTitle.classList.add("mx-translate", "mx-bar-title");
		creditBetTitle.setAttribute("data-lang", "bet_total");
		creditBet.appendChild(creditBetTitle);
		let creditBetValue = document.createElement("span");
		creditBetValue.id = ELEMENT_IDS.BET_CREDIT;
		creditBet.appendChild(creditBetValue);
		barGrey.appendChild(creditBet);

		let lines = document.createElement("div");
		lines.id = ELEMENT_IDS.LINES + "Container";
		lines.classList.add("mx-bar-lines");
		let linesTitle = document.createElement("span");
		linesTitle.classList.add("mx-translate", "mx-bar-title");
		linesTitle.setAttribute("data-lang", getTranslationName("line", gameset));
		lines.appendChild(linesTitle);
		let linesValue = document.createElement("span");
		linesValue.id = ELEMENT_IDS.LINES;
		lines.appendChild(linesValue);
		barGrey.appendChild(lines);

		let barBlack = document.createElement("div");
		barBlack.classList.add("mx-bar-black");
		barBlack.id = ELEMENT_IDS.BAR_BLACK;
		bar.appendChild(barBlack);

		if (
			this.config.get(CONFIG_PARAMS.MODE) === MODES.REAL ||
			this.config.get(CONFIG_PARAMS.LEGISLATION) === "pt"
		) {
			let time = document.createElement("div");
			time.classList.add("mx-bar-time", "mx-grey-text");
			barBlack.appendChild(time);
			let timeIco = document.createElement("span");
			timeIco.classList.add("mx-bar-time-ico");
			time.appendChild(timeIco);
			let timeValue = document.createElement("span");
			timeValue.id = BASE_ELEMENT_IDS.TIME;
			time.appendChild(timeValue);
		}

		let balance = document.createElement("div");
		balance.classList.add("mx-bar-balance");
		let balanceTitle = document.createElement("span");
		balanceTitle.classList.add("mx-translate", "mx-bar-title", "mx-grey-text");
		balanceTitle.setAttribute("data-lang", "balance");
		let balanceValue = document.createElement("span");
		balanceValue.id = ELEMENT_IDS.BALANCE;
		balance.appendChild(balanceTitle);
		balance.appendChild(balanceValue);
		barBlack.appendChild(balance);

		let bet = document.createElement("div");
		bet.classList.add("mx-bar-bet");
		this.betBarElement = bet;
		let betTitle = document.createElement("span");
		betTitle.classList.add("mx-translate", "mx-bar-title");
		betTitle.setAttribute("data-lang", "bet");
		bet.appendChild(betTitle);
		let betValue = document.createElement("span");
		betValue.id = ELEMENT_IDS.BET_REAL;
		bet.appendChild(betValue);
		barBlack.appendChild(bet);

		let win = document.createElement("div");
		win.classList.add("mx-bar-win");
		let winTitle = document.createElement("span");
		winTitle.classList.add("mx-translate", "mx-bar-title");
		winTitle.setAttribute("data-lang", "win");
		win.appendChild(winTitle);
		let winValue = document.createElement("span");
		winValue.id = ELEMENT_IDS.WIN_REAL;
		win.appendChild(winValue);
		barBlack.appendChild(win);

		if (this.config.get(CONFIG_PARAMS.LEGISLATION) === "it") {
			barBlack.append(this.createRespins())
		}

		let logo = document.createElement("img");
		logo.id = ELEMENT_IDS.BAR_LOGO;
		const isBellot = this.config.get(CONFIG_PARAMS.BRAND_NAME) === "bellot";
        const logoSrc = isBellot
            ? require("../../../../framework/assets/img/inline/ui/logo_new.svg")
            : require("../../../../framework/assets/img/inline/ui/logo_kajot.svg");
		logo.src = logoSrc;
		logo.classList.add("mx-bar-logo");
		barBlack.appendChild(logo);

		return bar;
	}

	createButtons(parent, btns: Record<string, HTMLElement>) {
		let container = document.createElement("div");
		container.id = BASE_ELEMENT_IDS.GAME_BUTTONS_CONTAINER;
		container.classList.add("mx-buttons-container");
		parent.appendChild(container);

		let spinCont = document.createElement("div");
		spinCont.id = ELEMENT_IDS.BUTTON_SPIN_WRAP;
		spinCont.classList.add("mx-button-spin-wrap");
		container.appendChild(spinCont);

		btns[BASE_ELEMENT_IDS.BUTTON_SPIN] = createButton({
			id: BASE_ELEMENT_IDS.BUTTON_SPIN,
			classes: [
				"mx-input",
				"mx-button",
				"mx-button-type-spin",
				"mx-button-mobile-spin",
			],
			action: ACTION_EVENTS.ACTION_SPIN,
			parent: spinCont,
		});

		btns[BASE_ELEMENT_IDS.BUTTON_SPIN_AUTOSTART] = createButton({
			id: BASE_ELEMENT_IDS.BUTTON_SPIN_AUTOSTART,
			classes: [
				"mx-input",
				"mx-button",
				"mx-button-type-spin",
				"mx-button-mobile-spin-autostart",
			],
			action: ACTION_EVENTS.ACTION_SPIN,
			parent: spinCont,
		});

		btns[BASE_ELEMENT_IDS.BUTTON_SKIP] = createButton({
			id: BASE_ELEMENT_IDS.BUTTON_SKIP,
			classes: [
				"mx-input",
				"mx-button",
				"mx-button-type-spin",
				"mx-button-mobile-skip",
			],
			action: ACTION_EVENTS.ACTION_SKIP,
			parent: spinCont,
		});

		btns[BASE_ELEMENT_IDS.BUTTON_STOP] = createButton({
			id: BASE_ELEMENT_IDS.BUTTON_STOP,
			classes: [
				"mx-input",
				"mx-button",
				"mx-button-type-spin",
				"mx-button-mobile-stop",
			],
			action: ACTION_EVENTS.ACTION_FORCESTOP,
			parent: spinCont,
		});

		btns[BASE_ELEMENT_IDS.BUTTON_STOP_AUTOSTART] = createButton({
			id: BASE_ELEMENT_IDS.BUTTON_STOP_AUTOSTART,
			classes: [
				"mx-input",
				"mx-button",
				"mx-button-type-spin",
				"mx-button-stop-autostart",
			],
			action: ACTION_EVENTS.ACTION_AUTOSTART_STOP,
			parent: spinCont,
		});

		btns[BASE_ELEMENT_IDS.BUTTON_STOP_BONUS_WHEEL] = createButton({
			id: BASE_ELEMENT_IDS.BUTTON_STOP_BONUS_WHEEL,
			classes: [
				"mx-input",
				"mx-button",
				"mx-button-type-spin",
				"mx-button-mobile-stop-wheel",
			],
			action: ACTION_EVENTS.ACTION_BONUS_WHEEL_STOP,
			parent: spinCont,
		});

		// btns[BASE_ELEMENT_IDS.BUTTON_FORCESPIN] = createButton({
		//     id: BASE_ELEMENT_IDS.BUTTON_FORCESPIN,
		//     classes: ["mx-input", "mx-button", "mx-button-type-spin", "mx-button-mobile-forcespin"],
		//     action: ACTION_EVENTS.ACTION_SPIN,
		//     parent: spinCont
		// });

		let bet = document.createElement("button");
		bet.id = ELEMENT_IDS.BUTTON_BET_OPEN;
		bet.classList.add("mx-input", "mx-button-bet", "mx-button");
		container.appendChild(bet);
		$(bet).data("action", ACTION_EVENTS.ACTION_BETMENU_OPEN);

		let freespinsBtn = document.createElement("button");
		freespinsBtn.id = ELEMENT_IDS.BUTTON_FREESPINS;
		freespinsBtn.classList.add("mx-input", "mx-button-freespins", "mx-button");
		container.appendChild(freespinsBtn);
		$(freespinsBtn).append(document.createElement("span"));
		$(freespinsBtn).data("action", MESSAGE_EVENTS.SWITCH_TO_FREESPINS);
		let curvedText = document.createElement("div");
		curvedText.textContent = translate(
			"freespins",
			this.config.get(CONFIG_PARAMS.LANGUAGE)
		);

		// @ts-ignore
		$(curvedText).lettering();
		$(freespinsBtn).append(curvedText);
		// plain label for the real-money direction; a div so walletSetBalance's children('span') keeps targeting the count
		let realLabel = document.createElement("div");
		realLabel.classList.add("mx-button-freespins-real-label");
		realLabel.textContent = translate(
			"freespins_choose_real_money",
			this.config.get(CONFIG_PARAMS.LANGUAGE)
		);
		$(freespinsBtn).append(realLabel);
		this.fsButton = freespinsBtn;
		$(freespinsBtn).hide();

		if (!this.config.get(CONFIG_PARAMS.DISABLE_AUTOSTART)) {
			let autostartContinue = document.createElement("button");
			autostartContinue.id = ELEMENT_IDS.BUTTON_AUTOSTART_CONTINUE;
			autostartContinue.classList.add(
				"mx-input",
				"mx-button",
				"mx-button-autostart-continue"
			);
			container.appendChild(autostartContinue);
			$(autostartContinue).data(
				"action",
				ACTION_EVENTS.ACTION_AUTOSTART_CONTINUE
			);
			$(autostartContinue).hide();

			let autostart = document.createElement("button");
			autostart.id = ELEMENT_IDS.BUTTON_AUTOSTART_OPEN;
			autostart.classList.add("mx-input", "mx-button-autostart", "mx-button");
			container.appendChild(autostart);
			$(autostart).data("action", ACTION_EVENTS.ACTION_AUTOSTART_OPEN);

		}

		let burger = document.createElement("button");
		burger.id = ELEMENT_IDS.BUTTON_BURGER;
		burger.classList.add("mx-input", "mx-button-burger", "mx-button");
		container.appendChild(burger);
		$(burger).data("action", ACTION_EVENTS.ACTION_BURGER_OPEN);

		this.createResponsibleGaming();
	}

	createResponsibleGaming() {
		let responsibleGamingIcon = document.createElement("button");
		responsibleGamingIcon.id = BASE_ELEMENT_IDS.BUTTON_RESPONSIBLE_GAMING;
		responsibleGamingIcon.classList.add(
			"mx-input",
			"mx-button-mobile-responsible-gaming"
		);
		$(responsibleGamingIcon).data(
			"action",
			ACTION_EVENTS.ACTION_RESPONSIBLE_GAMING
		);
		$(responsibleGamingIcon).hide();
		this.gameControlsWrapper.appendChild(responsibleGamingIcon);
	}

	isSwiperEnabled() {
		let disable = true; // disable by default
		if (
			this.config.get(CONFIG_PARAMS.DISABLE_SWIPER) == "0" ||
			(this.config.get(CONFIG_PARAMS.DISABLE_IOS_SWIPER) == "0" &&
				isIphoneSafari())
		) {
			disable = false;
		}

		// enable if not disabled and if not ios tablet and if not desktop and if not firefof and not ipad
		return (
			!disable &&
			getDeviceStr() !== "desktop" &&
			!isFirefox() &&
			!isIphoneChrome() &&
			!isIphoneFirefox() &&
			!isIpad()
		);
	}

	public hideFreeSpins() {
		this.freeSpinsShown = false;
		$(`.mx-bar-creditbalance .mx-bar-title[data-lang="credit"]`).text(translate("credit",this.config.get(CONFIG_PARAMS.LANGUAGE))).css({whiteSpace:"unset"})
		$(`#${ELEMENT_IDS.BAR_FREESPINS}`).hide();
		$("#" + ELEMENT_IDS.BUTTON_BET_OPEN).show();
		this.refreshFsButtonLabel();
		if (this.config.get(CONFIG_PARAMS.HIDE_FS_BET) && this.betBarElement) $(this.betBarElement).show();
	}

	/** FY-6: the FS button is a real <-> free spins toggle; label + action follow the mode, visibility stays with states. */
	public setFsButtonMode(mode: "real" | "freespins") {
		this.fsMode = mode;
		if (!this.fsButton) return;
		$(this.fsButton).data(
			"action",
			mode === "real" ? MESSAGE_EVENTS.SWITCH_TO_FREESPINS : MESSAGE_EVENTS.SWITCH_TO_REAL
		);
		this.fsButton.classList.toggle("mx-button-freespins-real", mode === "freespins");
	}

	public refreshFsButtonLabel() {
		this.setFsButtonMode(this.freeSpinsShown ? "freespins" : "real");
	}

	public showFreeSpins() {
		this.freeSpinsShown = true;
		if(this.config.get(CONFIG_PARAMS.USE_FS_TOTAL_WIN)){
			$(`.mx-bar-creditbalance .mx-bar-title[data-lang="credit"]`).text(translate("freespins_continue_total_win",this.config.get(CONFIG_PARAMS.LANGUAGE))).css({whiteSpace:"normal"})
		}
		$(`#${ELEMENT_IDS.BAR_FREESPINS}`).show();
		$("#" + ELEMENT_IDS.BUTTON_BET_OPEN).hide();
		this.refreshFsButtonLabel();
		// KAJOT-80: no bet size while in casino free spins
		if (this.config.get(CONFIG_PARAMS.HIDE_FS_BET) && this.betBarElement) $(this.betBarElement).hide();
	}

	fadeOutWin() {
		return new Promise<void>((res) => {
			$("#mxWinReal").css("opacity", 0);
			setTimeout(() => {
				$("#mxWinReal").css("opacity", 1);
				res();
			}, 200);
		});
	}

	highlightWin() {}

	resetWin() {}

	async winToBalance(run: boolean = true) {}

	animateLetter(i: number, el: HTMLElement) {}

	public isSettingsMenuAllowed() {
		return (
			!!this.settingsMenu &&
			(!isAndroid() || (isAndroid() && getAndroidVersion() >= 7))
		);
	}

	createRespins(): HTMLElement {
		this.respins = document.createElement('div')
		this.respins.classList.add("mx-bar-respin")
		this.respins.style.display = "none"
		return this.respins
	}

	showRespins(): void {
		if (this.respins)
			this.respins.style.display = "block"
	}

	updateRespins(respin: number, maxRespin: number, text: string = "Respins"): void {
		if (this.config.get(CONFIG_PARAMS.LEGISLATION) === "it") {
			if (respin > 0)
				this.respins.innerText = text + " " + respin.toString() + "/" + maxRespin.toString();
			else
				this.respins.innerText  = ''
		}
	}
}
